import * as React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { getThresholdKeyLabel } from '../../services/complianceTranslations';

function StatusChip({ label, tone }) {
  return <Chip size="small" label={label} color={tone} variant="outlined" />;
}

function formatDateTime(value) {
  const raw = String(value || '').trim();
  if (!raw || raw === '-') return '-';

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;

  return parsed.toLocaleString('pt-BR');
}

function asDateTimestamp(value) {
  const raw = String(value || '').trim();
  if (!raw || raw === '-') return 0;

  const parsed = Date.parse(raw);
  return Number.isNaN(parsed) ? 0 : parsed;
}

const disabledButtonSx = {
  '&.MuiButton-contained.Mui-disabled': {
    color: 'text.disabled',
    backgroundColor: 'action.disabledBackground',
    borderColor: 'action.disabledBackground',
    opacity: 1,
    WebkitTextFillColor: 'currentColor',
  },
};

export default function RulesPlanGrid({
  loading,
  errorMessage,
  saveMessage,
  saveErrorMessage,
  failedSaveItems = [],
  saving,
  savingRuleChanges = false,
  savingThresholdChanges = false,
  editable = false,
  hasPendingChanges = false,
  pendingChanges = [],
  pendingThresholdChanges = [],
  policy,
  rules,
  deactivationReasonsByRuleKey = {},
  groupChangeReason = '',
  thresholdValuesDraft = {},
  thresholdLimitsByKey = {},
  riskThresholdKeys = [],
  canManageGroupState = false,
  onToggleRule,
  onDeactivationReasonChange,
  onGroupChangeReasonChange,
  onEnableGroup,
  onDisableGroup,
  onThresholdValueChange,
  onDiscardChanges,
  onSaveRuleChanges,
  onSaveThresholdChanges,
}) {
  const [editTab, setEditTab] = React.useState('rules');
  const savingAny = Boolean(saving || savingRuleChanges || savingThresholdChanges);
  const hasEditableThresholdRules = rules.some((rule) => rule.canEditThresholds && rule.editableThresholdKeys.length);
  const hasEditableThresholds = hasEditableThresholdRules || riskThresholdKeys.length > 0;
  const isRuleLocked = (rule) => !rule.changeable || Boolean(rule.blockedReason);
  const changesToActivate = pendingChanges.filter((change) => change.nextActive);
  const changesToDeactivate = pendingChanges.filter((change) => !change.nextActive);
  const effectiveRules = React.useMemo(
    () => [...rules].sort((left, right) => asDateTimestamp(right.lastUpdatedAt) - asDateTimestamp(left.lastUpdatedAt)),
    [rules],
  );
  const getRuleKey = (change) => change?.code || change?.id;

  React.useEffect(() => {
    if (editTab === 'thresholds' && !hasEditableThresholds) {
      setEditTab('rules');
    }
  }, [editTab, hasEditableThresholds]);

  const buildThresholdHelperText = React.useCallback((key) => ([
    thresholdLimitsByKey[key]?.min !== null && thresholdLimitsByKey[key]?.min !== undefined
      ? `Min: ${thresholdLimitsByKey[key].min}`
      : null,
    thresholdLimitsByKey[key]?.max !== null && thresholdLimitsByKey[key]?.max !== undefined
      ? `Max: ${thresholdLimitsByKey[key].max}`
      : null,
    thresholdLimitsByKey[key]?.step !== null && thresholdLimitsByKey[key]?.step !== undefined
      ? `Passo: ${thresholdLimitsByKey[key].step}`
      : null,
    thresholdLimitsByKey[key]?.defaultValue !== null && thresholdLimitsByKey[key]?.defaultValue !== undefined
      ? `Padrão: ${thresholdLimitsByKey[key].defaultValue}`
      : null,
  ].filter(Boolean).join(' | ')), [thresholdLimitsByKey]);

  const renderThresholdField = React.useCallback((key, fieldKeyPrefix) => {
    const valueType = thresholdLimitsByKey[key]?.valueType;
    const fieldKey = `${fieldKeyPrefix}-${key}`;
    const commonProps = {
      label: getThresholdKeyLabel(key),
      size: 'small',
      value: thresholdValuesDraft[key] ?? '',
      onChange: (event) => onThresholdValueChange?.(key, event.target.value),
      disabled: savingAny,
      helperText: buildThresholdHelperText(key),
      sx: { minWidth: 260 },
    };

    if (valueType === 'boolean') {
      return (
        <TextField
          key={fieldKey}
          {...commonProps}
          select
        >
          <MenuItem value="true">Sim</MenuItem>
          <MenuItem value="false">Não</MenuItem>
        </TextField>
      );
    }

    return (
      <TextField
        key={fieldKey}
        {...commonProps}
        type={valueType === 'integer' || valueType === 'float' ? 'number' : 'text'}
        inputProps={{
          min: thresholdLimitsByKey[key]?.min ?? undefined,
          max: thresholdLimitsByKey[key]?.max ?? undefined,
          step: thresholdLimitsByKey[key]?.step ?? undefined,
        }}
      />
    );
  }, [buildThresholdHelperText, onThresholdValueChange, savingAny, thresholdLimitsByKey, thresholdValuesDraft]);

  if (loading) {
    return (
      <Card variant="outlined" sx={{ width: '100%' }}>
        <CardContent>
          <Stack sx={{ alignItems: 'center', py: 6 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
              Carregando regras da policy...
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
      {saveMessage ? <Alert severity="success">{saveMessage}</Alert> : null}
      {saveErrorMessage ? <Alert severity="warning">{saveErrorMessage}</Alert> : null}
      {savingRuleChanges || savingThresholdChanges ? (
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          {savingRuleChanges ? (
            <Chip size="small" color="info" variant="outlined" label="Salvando regras" />
          ) : null}
          {savingThresholdChanges ? (
            <Chip size="small" color="info" variant="outlined" label="Salvando limiares" />
          ) : null}
        </Stack>
      ) : null}
      {failedSaveItems.length ? (
        <Alert severity="warning">
          <Stack spacing={1}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Falhas por regra
            </Typography>
            {failedSaveItems.map((item) => (
              <Typography key={`${item.code}-${item.status || 'no-status'}`} variant="body2" sx={{ color: 'text.secondary' }}>
                {item.code} | Status: {item.status || 'N/A'} | {item.message}
              </Typography>
            ))}
          </Stack>
        </Alert>
      ) : null}

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1}>
            <Typography component="h1" variant="h5">
              Regras da Policy
            </Typography>
            {policy ? (
              <>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Plano: {policy.name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Versão: {policy.version}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Cobertura: {policy.coverage}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Nenhuma política ativa encontrada no momento.
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      {editable ? (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Tabs
                value={editTab}
                onChange={(_, value) => setEditTab(value)}
                aria-label="Abas de edição da policy"
              >
                <Tab
                  value="rules"
                  label={`Regras (${pendingChanges.length})`}
                  disableRipple
                />
                <Tab
                  value="thresholds"
                  label={`Limiares (${pendingThresholdChanges.length})`}
                  disabled={!hasEditableThresholds}
                  disableRipple
                />
              </Tabs>

              {editTab === 'rules' ? (
                <Stack spacing={2}>
                  <Box>
                    <Typography component="h2" variant="h6">
                      Edição de regras
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Alterações pendentes: {pendingChanges.length}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      color="success"
                      variant="outlined"
                      label={`Para ativar: ${changesToActivate.length}`}
                    />
                    <Chip
                      size="small"
                      color="warning"
                      variant="outlined"
                      label={`Para desativar: ${changesToDeactivate.length}`}
                    />
                  </Stack>

                  {canManageGroupState ? (
                    <Stack spacing={1}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Ação por grupo
                      </Typography>
                      <TextField
                        label="Justificativa para alteração do grupo"
                        value={groupChangeReason}
                        onChange={(event) => onGroupChangeReasonChange?.(event.target.value)}
                        required
                        size="small"
                        fullWidth
                        disabled={savingAny}
                        helperText="Obrigatória para ativar ou desativar todas as regras do grupo."
                      />
                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                        <Button
                          variant="outlined"
                          color="secondary"
                          disableRipple
                          onClick={onEnableGroup}
                          disabled={savingAny}
                        >
                          Ativar grupo
                        </Button>
                        <Button
                          variant="contained"
                          color="secondary"
                          disableRipple
                          onClick={onDisableGroup}
                          disabled={savingAny}
                          sx={disabledButtonSx}
                        >
                          Desativar grupo
                        </Button>
                      </Stack>
                    </Stack>
                  ) : null}

                  {pendingChanges.length ? (
                    <Stack spacing={1}>
                      {changesToActivate.length ? (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Ativar: {changesToActivate.map((change) => change.code).join(', ')}
                        </Typography>
                      ) : null}
                      {changesToDeactivate.length ? (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Desativar: {changesToDeactivate.map((change) => change.code).join(', ')}
                        </Typography>
                      ) : null}
                      {pendingChanges.map((change) => (
                        <Typography key={change.id} variant="body2" sx={{ color: 'text.secondary' }}>
                          {change.code}: {change.nextActive ? 'ativar' : 'desativar'}
                        </Typography>
                      ))}
                    </Stack>
                  ) : (
                    <Alert severity="info">Nenhuma alteração pendente.</Alert>
                  )}

                  {changesToDeactivate.length ? (
                    <Stack spacing={1}>
                      {changesToDeactivate.map((change) => {
                        const ruleKey = getRuleKey(change);
                        const ruleTitle = change?.rule?.title || change?.rule?.raw?.title || 'Regra sem descrição';

                        return (
                          <Stack key={`reason-${ruleKey}`} spacing={0.75}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {change.code || change.id}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {ruleTitle}
                            </Typography>
                            <TextField
                              label={`Justificativa para desativação (${change.code || change.id})`}
                              value={deactivationReasonsByRuleKey[ruleKey] || ''}
                              onChange={(event) => onDeactivationReasonChange?.(ruleKey, event.target.value)}
                              required
                              size="small"
                              fullWidth
                              disabled={savingAny}
                              helperText="Obrigatório quando houver regras sendo desativadas."
                            />
                          </Stack>
                        );
                      })}
                    </Stack>
                  ) : null}

                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    <Button variant="outlined" disableRipple onClick={onDiscardChanges} disabled={savingAny || !hasPendingChanges}>
                      Descartar alterações
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      disableRipple
                      onClick={onSaveRuleChanges}
                      disabled={savingAny || !pendingChanges.length}
                      sx={disabledButtonSx}
                    >
                      {savingRuleChanges ? 'Salvando regras...' : 'Salvar regras'}
                    </Button>
                  </Stack>
                </Stack>
              ) : null}

              {editTab === 'thresholds' ? (
                <Stack spacing={2}>
                  <Box>
                    <Typography component="h2" variant="h6">
                      Edição de limiares
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Campos renderizados por regra com base nas chaves editáveis de limiares.
                    </Typography>
                  </Box>

                  {pendingThresholdChanges.length ? (
                    <Alert severity="info">
                      Alterações de limiares pendentes: {pendingThresholdChanges.length}
                    </Alert>
                  ) : (
                    <Alert severity="info">Nenhuma alteração de limiares pendente.</Alert>
                  )}

                  {hasEditableThresholds ? (
                    <Stack spacing={2}>
                      {riskThresholdKeys.length ? (
                        <Card variant="outlined">
                          <CardContent>
                            <Stack spacing={2}>
                              <Typography variant="subtitle2">
                                Classificação de risco da policy
                              </Typography>
                              <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
                                {riskThresholdKeys.map((key) => renderThresholdField(key, 'risk-threshold'))}
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      ) : null}

                      {rules
                        .filter((rule) => rule.canEditThresholds && rule.editableThresholdKeys.length)
                        .map((rule) => (
                          <Card key={`thresholds-${rule.id}`} variant="outlined">
                            <CardContent>
                              <Stack spacing={2}>
                                <Typography variant="subtitle2">
                                  {rule.code} - {rule.title}
                                </Typography>
                                <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
                                  {rule.editableThresholdKeys.map((key) => (
                                    renderThresholdField(key, rule.id)
                                  ))}
                                </Stack>
                              </Stack>
                            </CardContent>
                          </Card>
                        ))}

                    </Stack>
                  ) : (
                    <Alert severity="info">Nenhum limiar editável disponível no contexto atual.</Alert>
                  )}

                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      color="secondary"
                      disableRipple
                      onClick={onSaveThresholdChanges}
                      disabled={savingAny || !pendingThresholdChanges.length || !hasEditableThresholds}
                      sx={disabledButtonSx}
                    >
                      {savingThresholdChanges ? 'Salvando limiares...' : 'Salvar limiares'}
                    </Button>
                  </Stack>
                </Stack>
              ) : null}
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Box>
              <Typography component="h2" variant="h6">
                Regras efetivas
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Lista read-only das regras aplicadas ao plano do cliente.
              </Typography>
            </Box>

            <Divider />

            {!rules.length ? (
              <Alert severity="info">Nenhuma regra encontrada para a política atual.</Alert>
            ) : (
              <TableContainer>
                <Table size="small" aria-label="regras do plano">
                  <TableHead>
                    <TableRow>
                      <TableCell>Código</TableCell>
                      <TableCell>Título</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Alteração</TableCell>
                      <TableCell>Restrição</TableCell>
                      <TableCell>Última atualização</TableCell>
                      <TableCell>Atualizado por</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {effectiveRules.map((rule) => (
                      <TableRow key={rule.id} hover>
                        <TableCell>{rule.code}</TableCell>
                        <TableCell>{rule.title}</TableCell>
                        <TableCell>
                          <StatusChip
                            label={rule.active ? 'Ativa' : 'Inativa'}
                            tone={rule.active ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          {editable && !isRuleLocked(rule) ? (
                            <FormControlLabel
                              control={(
                                <Switch
                                  checked={rule.active}
                                  onChange={() => onToggleRule?.(rule.id)}
                                  disabled={savingAny}
                                  disableRipple
                                />
                              )}
                              label={rule.active ? 'Ativa' : 'Inativa'}
                            />
                          ) : (
                            <StatusChip
                              label={rule.blockedReason ? 'Bloqueada' : rule.changeable ? 'Alterável' : 'Bloqueada'}
                              tone={rule.blockedReason ? 'warning' : rule.changeable ? 'info' : 'warning'}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {rule.blockedReason || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {formatDateTime(rule.lastUpdatedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {rule.lastUpdatedBy || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
